import { useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { PageHeader, Loading, DownloadButton } from "@/components/Common";
import { SimpleSelect } from "@/components/Filters";
import { useLoad } from "@/hooks/useData";
import { usePref } from "@/hooks/usePref";
import { api } from "@/lib/api";
import { fmtINR, fmtNum, monthLabel, todayStr } from "@/lib/format";

const iso = (d) => d.toISOString().slice(0, 10);
const range = (k, custom) => {
  const t = new Date(todayStr()); const y = t.getFullYear(), m = t.getMonth();
  if (k === "last_month") return [iso(new Date(Date.UTC(y, m - 1, 1))), iso(new Date(Date.UTC(y, m, 0)))];
  if (k === "90") return [iso(new Date(t - 89 * 864e5)), todayStr()];
  if (k === "fy") return ["2026-04-01", "2027-03-31"];
  if (k === "custom") return [custom.from || todayStr(), custom.to || todayStr()];
  return [iso(new Date(Date.UTC(y, m, 1))), todayStr()];
};

const Kpi = ({ label, value, to, tone = "", testid }) => (
  <Link to={to} className="panel block p-4 transition-colors hover:border-primary/50 fade-up">
    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`mt-3 font-mono text-2xl font-extrabold ${tone}`} data-testid={testid}>{value}</div>
  </Link>
);

const Bars = ({ title, obj, testid, money }) => {
  const rows = Object.entries(obj || {}); const max = Math.max(1, ...rows.map(([, v]) => v));
  return (
    <div className="panel p-4" data-testid={testid}>
      <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{title}</h3>
      {!rows.length ? <div className="text-xs text-muted-foreground">No data</div> : rows.map(([k, v]) => (
        <div key={k} className="mb-1.5"><div className="flex justify-between text-xs"><span className="truncate pr-2">{k}</span><span className="font-mono font-bold">{money ? fmtINR(v) : fmtNum(v)}</span></div>
          <div className="h-1.5 rounded bg-muted"><div className="h-1.5 rounded bg-primary" style={{ width: `${(v / max) * 100}%` }} /></div></div>))}
    </div>
  );
};

export default function ReturnsDashboard() {
  const [period, setPeriod] = usePref("returns.dash.period", "this_month");
  const [custom, setCustom] = useState({});
  const [from, to] = range(period, custom);
  const { data } = useLoad(() => api.get("/returns-dashboard", { params: { date_from: from, date_to: to } }).then((r) => r.data), [from, to]);
  return (
    <div data-testid="returns-dashboard">
      <PageHeader title="Returns Dashboard" subtitle={`Returns dated ${from} to ${to}. Open cases and queues are always live.`}>
        <SimpleSelect value={period} onChange={setPeriod} testid="rdash-period" options={[["this_month", "This month"], ["last_month", "Last month"], ["90", "Last 90 days"], ["fy", "FY 2026-27"], ["custom", "Custom"]]} />
        {period === "custom" && <><Input type="date" className="h-9 w-36" value={custom.from || ""} onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="rdash-from" />
          <Input type="date" className="h-9 w-36" value={custom.to || ""} onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="rdash-to" /></>}
        <DownloadButton path="/returns-report" params={{ date_from: from, date_to: to }} testid="rdash-report-btn" label="Monthly Return Report" />
      </PageHeader>
      {!data ? <Loading /> : <>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Kpi label="Returns in period" value={fmtNum(data.kpi.in_period)} to={`/returns/register`} testid="rkpi-period" />
          <Kpi label="Open cases" value={fmtNum(data.kpi.open)} to="/returns/queue" tone="text-amber-600" testid="rkpi-open" />
          <Kpi label="Unmatched" value={fmtNum(data.kpi.unmatched)} to="/returns/queue" tone="text-red-600" testid="rkpi-unmatched" />
          <Kpi label={`Open > ${data.settings.red_days} days`} value={fmtNum(data.kpi.over_red)} to="/returns/queue" tone="text-red-600" testid="rkpi-old" />
          <Kpi label="Refund pending ₹" value={fmtINR(data.kpi.refund_pending)} to="/returns/queue" testid="rkpi-refund" />
          <Kpi label="Claims open ₹" value={fmtINR(data.kpi.claims_open)} to="/returns/claims" tone="text-purple-700" testid="rkpi-claims" />
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <Bars title="Case status (all)" obj={data.by_status} testid="rpanel-status" />
          <Bars title="Work queue by owner" obj={data.by_owner} testid="rpanel-owner" />
          <Bars title="Ageing of open cases (days)" obj={data.ageing} testid="rpanel-ageing" />
          <Bars title="By courier" obj={data.by_courier} testid="rpanel-courier" />
          <Bars title="By return type" obj={data.by_type} testid="rpanel-type" />
          <Bars title="By channel" obj={data.by_channel} testid="rpanel-channel" />
          <Bars title="Customer return reasons" obj={data.by_reason} testid="rpanel-reason" />
          <Bars title="Condition on inspection" obj={data.by_condition} testid="rpanel-condition" />
          <Bars title="Money in period" obj={data.money} money testid="rpanel-money" />
          <Bars title="Data health (all returns)" obj={data.health} testid="rpanel-health" />
          <Bars title="Top 10 returned products" obj={Object.fromEntries(data.top_products.map((t) => [t.product, t.returns]))} testid="rpanel-top" />
          <div className="panel p-4" data-testid="rpanel-monthly">
            <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Returns by month · {data.fy}</h3>
            <ResponsiveContainer width="100%" height={200}><BarChart data={data.monthly.map((m) => ({ ...m, label: monthLabel(m.month).slice(0, 3) }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip />
              <Bar dataKey="returns" fill="#E11D2E" radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer>
          </div>
        </div>
      </>}
    </div>
  );
}
